import React, { useState, useEffect } from "react"
import { graphql, Link } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

import Layout from "../components/layout"
import Seo from "../components/seo"
import Hero from "../components/hero"
import Container from "../components/container"

// local components
const Card = ({ title, date, cover, type, slug, isOpen }) => {
  const seminarDate = new Date(Date.parse(date))
  const formattedDate = `${seminarDate.getFullYear()}.${
    seminarDate.getMonth() + 1
  }.${seminarDate.getDate()}`

  const formatType = type => {
    switch (type) {
      case "seminar":
        return "セミナー"
      case "video":
        return "動画"
    }
  }

  return (
    <div className="rounded-xl mb-3 lg:mb-0 shadow-lg overflow-hidden hover:shadow-xl">
      <Link
        to={`/seminar${slug}`}
        target="_blank"
        itemProp="url"
        className="hover:opacity-80 block h-full lg:flex flex-col"
      >
        {cover && (
          <GatsbyImage image={getImage(cover)} alt={title} className="" />
        )}
        <div className="p-6 flex flex-col justify-between flex-grow">
        <div className="text-textGray-60 text-sm mb-2">{formatType(type)}</div>
          <h2 className="text-base font-bold text-brandNavy hover:opacity-80 mb-3">
            {title}
          </h2>
          <div className="flex justify-between items-center">
            {type === "seminar" ? (
              <div className="text-textGray-60 text-sm">{formattedDate}</div>
            ) : (
              <div className="text-textGray-60 text-sm"></div>
            )}
            {isOpen ? (
              <span className="text-white bg-blue-400 rounded-full text-sm font-bold py-1 px-2 inline-block">
                受付中
              </span>
            ) : (
              <span className="text-white bg-textGray-60 rounded-full text-sm font-bold py-1 px-2 inline-block">
                受付終了
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  )
}

// page content
const Seminar = ({ data, location }) => {
  const allSeminarList = data.allMarkdownRemark.nodes
  const [filter, setFilter] = useState("all")
  const [modifyedAllSeminarList, refreshSeminarList] = useState([])
  const [page, setPage] = useState(1)
  const PER_PAGE = 12

  useEffect(() => {
    const url = new URL(window.location.href)
    const param = url.searchParams
    setFilter(param.get("filter") || "all")
    // TODO: URLパラメータの更新

    const today = new Date()
    allSeminarList.forEach(seminar => {
      const closeDate = new Date(Date.parse(seminar.frontmatter.date))
      closeDate.setMinutes(closeDate.getMinutes() - 30)
      seminar["isOpen"] = closeDate > today
    })
    refreshSeminarList(allSeminarList.sort((a, b) => b.isOpen - a.isOpen))
  }, [allSeminarList])

  const filteredSeminarList = filterStatus => {
    if (filterStatus === "all") return modifyedAllSeminarList
    return modifyedAllSeminarList.filter(
      seminar => seminar.isOpen === (filterStatus === "accepting")
    )
  }

  const hasNextPage = () => {
    return page * PER_PAGE < filteredSeminarList(filter).length
  }

  const loadNextPage = () => {
    if (!hasNextPage()) {
      return
    }
    setPage(page + 1)
  }

  return (
    <Layout location={location}>
      <Seo
        title="セミナー・動画"
        description="CDP「INTEGRAL-CORE」を提供するEVERRISEが開催するセミナーの一覧です。"
      />
      <Hero type="secondary">
        <div className="lg:w-9/12 lg:m-auto pt-10 lg:pt-32 pb-20 lg:pb-28 text-center">
          <h1 className="font-bold text-2xl lg:text-4xl mb-4 lg:mb-5">
            セミナー・動画
          </h1>
          <p className="text-xs lg:text-base text-textGray-100 leading-relaxed lg:leading-relaxed text-left">
            INTEGRAL-COREは、セミナーやイベントを定期的に開催しています。最新のCDP事例や顧客データの活用方法、マーケティングノウハウをお伝えします。費用は無料ですので、ぜひご参加ください。
          </p>
        </div>
      </Hero>
      <div className="relative z-30">
        <Container>
          <div className="rounded-lg bg-white p-2 lg:p-3 shadow-xl flex items-center justify-between lg:w-9/12 lg:mx-auto -mt-5 lg:-mt-11 -mx-3 text-center lg:gap-1">
            <button
              className={`block font-bold rounded-lg flex-grow text-xs lg:text-lg py-3 hover:bg-blue-200 cursor-pointer ${
                filter === "all" && "bg-blue-200"
              }`}
              onClick={() => {
                if (filter === "all") return
                setFilter("all")
                setPage(1)
              }}
            >
              すべて
            </button>
            <button
              className={`block font-bold rounded-lg flex-grow text-xs lg:text-lg py-3 hover:bg-blue-200 cursor-pointer ${
                filter === "accepting" && "bg-blue-200"
              }`}
              onClick={() => {
                if (filter === "accepting") return
                setFilter("accepting")
                setPage(1)
              }}
            >
              受付中
            </button>
            <button
              className={`block font-bold rounded-lg flex-grow text-xs lg:text-lg py-3 hover:bg-blue-200 cursor-pointer ${
                filter === "end" && "bg-blue-200"
              }`}
              onClick={() => {
                if (filter === "end") return
                setFilter("end")
                setPage(1)
              }}
            >
              受付終了
            </button>
          </div>
        </Container>
      </div>
      <section className="py-12 lg:py-24" id="product">
        <Container>
          {filteredSeminarList(filter).length === 0 ? (
            <div className="lg:w-9/12 lg:mx-auto">セミナーがありません</div>
          ) : (
            <div className="lg:grid lg:grid-cols-3 gap-8">
              {filteredSeminarList(filter)
                .slice(0, page * PER_PAGE)
                .map(seminar => {
                  return (
                    <Card
                      key={seminar.id}
                      title={seminar.frontmatter.title}
                      cover={seminar.frontmatter.cover}
                      type={seminar.frontmatter.type}
                      date={seminar.frontmatter.date}
                      slug={seminar.fields.slug}
                      isOpen={seminar.isOpen}
                    />
                  )
                })}
            </div>
          )}
          {hasNextPage() && (
            <div className="text-center p-6">
              <button
                className={`text-linkBlue bg-white font-bold rounded text-sm lg:text-base lg:w-52 min-w-max px-3 py-3 lg:py-4 lg:px-4 text-center hover:opacity-90 inline-block hover:cursor-pointer`}
                onClick={() => loadNextPage()}
              >
                もっと見る
              </button>
            </div>
          )}
        </Container>
      </section>
    </Layout>
  )
}

export default Seminar

export const pageQuery = graphql`
  query {
    allMarkdownRemark(
      sort: { fields: [frontmatter___type, frontmatter___date], order: [ASC, DESC] }
      filter: { fields: { collection: { eq: "seminar" } } }
      limit: 2000
    ) {
      nodes {
        frontmatter {
          type
          date
          title
          cover {
            childImageSharp {
              gatsbyImageData(width: 640)
            }
          }
        }
        fields {
          slug
        }
        id
      }
    }
  }
`
